import React from 'react';

/**
 * Card Component
 *
 * Versatile container component using the Coord design system.
 * Supports multiple variants for different contexts.
 *
 * @example
 * <Card variant="white" padding="lg">Content</Card>
 * <Card variant="highlight" hover clickable onClick={handleClick}>Interactive</Card>
 */
const Card = ({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
  shadow = 'sm',
  hover = false,
  clickable = false,
  onClick,
  as: Component = 'div',
  ...props
}) => {
  const cn = (...classes) => classes.filter(Boolean).join(' ');

  const baseClasses = [
    'rounded-xl transition-all duration-300 ease-smooth',
    clickable && 'cursor-pointer',
    hover && 'hover:shadow-md hover:-translate-y-0.5',
    clickable && 'active:scale-[0.99]'
  ].filter(Boolean).join(' ');

  // Variant styles using design system tokens
  const variantClasses = {
    // Default - warm cream background
    default: 'bg-background-card border border-border-light',

    // White - clean white background
    white: 'bg-white border border-border-light',

    // Transparent - no background
    transparent: 'bg-transparent',

    // Primary/Highlight - peach tint for emphasis
    primary: 'bg-primary-50 border border-primary-200',
    highlight: 'bg-primary-50 border border-primary-200',

    // Sage - green tint for success/completion states
    sage: 'bg-sage-50 border border-sage-200',
    success: 'bg-success-50 border border-success-200',

    // Warning - amber tint
    warning: 'bg-warning-50 border border-warning-200',

    // Error - red tint
    error: 'bg-error-50 border border-error-200',

    // Muted - subtle neutral background
    muted: 'bg-neutral-50 border border-neutral-200',

    // Elevated - white with more prominent shadow
    elevated: 'bg-white border border-border-light shadow-md',

    // Glass - frosted glass effect
    glass: 'bg-white/70 backdrop-blur-md border border-white/30',
  };

  const paddingClasses = {
    none: 'p-0',
    xs: 'p-2',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10'
  };

  const shadowClasses = {
    none: 'shadow-none',
    sm: 'shadow-sm',
    md: 'shadow',
    lg: 'shadow-lg',
    xl: 'shadow-xl'
  };

  // Add focus styles for clickable cards
  const focusClasses = clickable
    ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
    : '';

  return (
    <Component
      onClick={clickable ? onClick : undefined}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick?.(e);
        }
      } : undefined}
      className={cn(
        baseClasses,
        variantClasses[variant],
        paddingClasses[padding],
        shadowClasses[shadow],
        focusClasses,
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
};

export default Card;
